import type {ReactElement} from 'react';
import {useState} from 'react';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {getAiChatSeasonSuggestionSet, getNextAiChatSeasonSuggestionSetIndex} from '@AiChat/lib/ai-chat-season-library';
import type {AiChatSeasonSuggestion} from '@AiChat/lib/ai-chat-season-library.types';
import type {AiChatSeasonSuggestionPillsProps} from './ai-chat-season-suggestion-pills.types';
import seasonStyles from '@AiChat/ai-chat-season.module.scss';

export function AiChatSeasonSuggestionPills({season}: AiChatSeasonSuggestionPillsProps): ReactElement {
  const {state} = useAiChat();
  const {actions: promptBoxActions} = useAiPromptBox(state.promptBoxId);
  const [suggestionSetIndex, setSuggestionSetIndex] = useState<number>(0);

  const fillPromptBoxWithSuggestion = (suggestion: AiChatSeasonSuggestion): void => {
    promptBoxActions.update({
      prompt: window.i18next.t(suggestion.promptKey),
      promptBoxState: PromptBoxState.ACTIVE,
    });
  };

  const showNextSuggestionSet = (): void => {
    setSuggestionSetIndex(getNextAiChatSeasonSuggestionSetIndex(season, suggestionSetIndex));
  };

  const getSuggestionPill = (suggestion: AiChatSeasonSuggestion): ReactElement => {
    return (
      <PillButton
        key={suggestion.titleKey}
        size={PillButtonSize.MEDIUM}
        text={`${suggestion.emoji} ${window.i18next.t(suggestion.titleKey)}`}
        onClick={(): void => {
          fillPromptBoxWithSuggestion(suggestion);
        }}
      />
    );
  };

  const getSuggestionPills = (): ReactElement[] => {
    return getAiChatSeasonSuggestionSet(season, suggestionSetIndex).map(getSuggestionPill);
  };

  return (
    <div className={`flex-h-row flex-center gap-2 ${seasonStyles.suggestionPills}`}>
      {getSuggestionPills()}
      <Icon
        icon="icon-rotate-ccw"
        type={IconType.SECONDARY}
        shape={IconShape.CIRCLE}
        size={IconSize.SIZE_ICON_20}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_refresh')}
        onClick={showNextSuggestionSet}
      />
    </div>
  );
}
